import analyticsService from './analyticsService';
import { supabase } from '../lib/supabaseClient';

class ConversionTrackingService {
  // Track conversion in database for internal analytics
  async logConversion(conversionData) {
    try {
      const { data, error } = await supabase?.from('conversion_events')?.insert([{
          conversion_type: conversionData?.type,
          user_id: conversionData?.userId,
          value: conversionData?.value || 0,
          metadata: conversionData?.metadata || {},
          created_at: new Date()?.toISOString(),
        }]);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error logging conversion:', error);
      return { success: false, error };
    }
  }

  // Registration Funnel Tracking
  async trackRegistrationFunnel(step, userId, userType, metadata = {}) {
    try {
      // Track in GA4
      analyticsService?.trackEvent('registration_funnel', {
        funnel_step: step,
        user_type: userType,
        ...metadata,
      });

      // Log in database
      await supabase?.from('funnel_events')?.insert([{
        funnel_type: 'registration',
        step: step,
        user_id: userId,
        user_type: userType,
        metadata: metadata,
        created_at: new Date()?.toISOString(),
      }]);

      return { success: true };
    } catch (error) {
      console.error('Error tracking registration funnel:', error);
      return { success: false, error };
    }
  }

  // Job Application Funnel Tracking
  async trackApplicationFunnel(step, userId, jobId, metadata = {}) {
    try {
      // Track in GA4
      analyticsService?.trackEvent('application_funnel', {
        funnel_step: step,
        job_id: jobId,
        ...metadata,
      });

      // Log in database
      await supabase?.from('funnel_events')?.insert([{
        funnel_type: 'job_application',
        step: step,
        user_id: userId,
        job_id: jobId,
        metadata: metadata,
        created_at: new Date()?.toISOString(),
      }]);

      return { success: true };
    } catch (error) {
      console.error('Error tracking application funnel:', error);
      return { success: false, error };
    }
  }

  // Subscription Funnel Tracking
  async trackSubscriptionFunnel(step, userId, planName, metadata = {}) {
    try {
      // Track in GA4
      analyticsService?.trackEvent('subscription_funnel', {
        funnel_step: step,
        plan_name: planName,
        ...metadata,
      });

      // Log in database
      await supabase?.from('funnel_events')?.insert([{
        funnel_type: 'subscription',
        step: step,
        user_id: userId,
        metadata: { plan_name: planName, ...metadata },
        created_at: new Date()?.toISOString(),
      }]);

      return { success: true };
    } catch (error) {
      console.error('Error tracking subscription funnel:', error);
      return { success: false, error };
    }
  }

  // Get conversion metrics
  async getConversionMetrics(conversionType, startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('conversion_events')?.select('*')?.eq('conversion_type', conversionType)?.gte('created_at', startDate)?.lte('created_at', endDate);

      if (error) throw error;

      return {
        success: true,
        total: data?.length || 0,
        totalValue: data?.reduce((sum, item) => sum + (item?.value || 0), 0) || 0,
        data: data,
      };
    } catch (error) {
      console.error('Error getting conversion metrics:', error);
      return { success: false, error };
    }
  }

  // Get funnel metrics
  async getFunnelMetrics(funnelType, startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('funnel_events')?.select('*')?.eq('funnel_type', funnelType)?.gte('created_at', startDate)?.lte('created_at', endDate);

      if (error) throw error;

      // Calculate funnel drop-off rates
      const stepCounts = {};
      data?.forEach(event => {
        stepCounts[event.step] = (stepCounts?.[event?.step] || 0) + 1;
      });

      return {
        success: true,
        stepCounts: stepCounts,
        data: data,
      };
    } catch (error) {
      console.error('Error getting funnel metrics:', error);
      return { success: false, error };
    }
  }
}

export default new ConversionTrackingService();